import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  FormControl, Select, MenuItem, IconButton, Tooltip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles(() => ({
  root: {
    padding: '12px 16px 8px 16px',
    background: '#f7fafd',
    borderBottom: '1px solid #e0e0e0',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  select: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-input': {
      padding: '8px 8px',
      fontSize: 13,
    },
  },
  iconBtn: {
    border: '1px solid #d0d0d0',
    borderRadius: 6,
    padding: 6,
    background: '#fff',
    '&:hover': { background: '#f0f0f0' },
  },
  nameField: {
    marginTop: 6,
    '& .MuiOutlinedInput-input': {
      padding: '6px 8px',
      fontSize: 13,
    },
  },
}));

const TakeoffSelector = ({
  takeoffs = [],
  activeTakeoffId,
  activeTakeoffName = '',
  onSwitchTakeoff,
  onNewTakeoff,
  onDeleteTakeoff,
  onRenameTakeoff,
  disabled,
}) => {
  const classes = useStyles();
  const nameInputRef = useRef(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!activeTakeoffName || activeTakeoffId) return;
    const input = nameInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [activeTakeoffId, activeTakeoffName]);

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <Select
            value={activeTakeoffId || ''}
            onChange={(e) => onSwitchTakeoff(e.target.value)}
            displayEmpty
            disabled={disabled}
          >
            {!activeTakeoffId && (
              <MenuItem value="" disabled>
                <em>{takeoffs.length ? 'Select a takeoff' : 'No takeoffs yet'}</em>
              </MenuItem>
            )}
            {takeoffs.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.name || 'Untitled'} ({t.areaCount} areas)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="New takeoff">
          <span>
            <IconButton
              size="small"
              onClick={onNewTakeoff}
              className={classes.iconBtn}
              disabled={disabled}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {activeTakeoffId && (
          <Tooltip title="Delete takeoff">
            <span>
              <IconButton
                size="small"
                onClick={() => setDeleteOpen(true)}
                className={classes.iconBtn}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
      {(activeTakeoffId || activeTakeoffName) && (
        <TextField
          className={classes.nameField}
          fullWidth
          variant="outlined"
          size="small"
          value={activeTakeoffName}
          onChange={(e) => onRenameTakeoff(e.target.value)}
          placeholder="Takeoff name"
          disabled={disabled}
          inputRef={nameInputRef}
          onFocus={(e) => e.target.select()}
        />
      )}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete takeoff?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete "{activeTakeoffName || 'this takeoff'}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              setDeleteOpen(false);
              onDeleteTakeoff(activeTakeoffId);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TakeoffSelector;
